<template>
  <div class="room-meeting-note-modal-component flex flex-col border border-gray-300">
    <div class="h-20 px-6 border-b flex justify-between items-center">
      <div style="font-size: 28px; color: #1D242A;" class="font-bold leading-9">{{ `AI ${this.$t('extendMenu.meetingNote.title')}` }}</div>
      <button class="w-6 h-6" @click="onClose">
        <img :src="require('@/assets/images/x_gray.svg')" />
      </button>
    </div>
    <div v-if="meetingNoteList.length > 0"
         class="p-6 pb-0 flex justify-end items-center flex-wrap gap-2 relative lg:gap-3"
    >
      <button style="height: 44px;"
              class="btn pr-3 rounded-lg flex items-center text-white text-sm font-bold"
              @click="onStartNewMeetingNote"
      >
        <div style="width: 44px; height: 44px;"
             class="flex justify-center items-center pointer-events-none"
        >
          <img width="24" height="24" :src="require('@/assets/images/meeting-note/add_white.svg')" />
        </div>
        {{ this.$t('extendMenu.meetingNote.createMeetingNote') }}
      </button>
    </div>
    <div v-if="ongoingMeetingNoteList && ongoingMeetingNoteList.length"
         class="px-6 pb-10 border-b"
    >
      <div class="mt-4">
        <RoomMeetingNoteList :meetingNoteList="ongoingMeetingNoteList"
                             :showDate="false"
                             @click="onClickMeetingNote"
        />
      </div>
    </div>
    <div class="flex-1 px-6 flex flex-col"
         :class="{
           'pt-10': ongoingMeetingNoteList && ongoingMeetingNoteList.length
         }"
    >
      <div class="flex items-center gap-x-2">
        <div v-if="chuckedMeetingNoteList" class="relative">
          <button style="width: 108px; border-color: #C3CCD5;"
                  class="h-9 rounded-lg border px-2 flex justify-between items-center relative"
                  @click="showSortOption = !showSortOption"
          >
            <span style="color: #1D242A;" class="text-left text-sm font-medium">{{ selectedSortOptionTitle }}</span>
            <img class="pointer-events-none" :src="require('@/assets/images/arrow_triangle_down.svg')" />
          </button>

          <div style="width: 108px; border-color: #E6EBEF; box-shadow: 0px 2px 16px 0px rgba(0, 0, 0, 0.16);"
               class="bg-white rounded-lg border absolute top-10 left-0 z-10 overflow-hidden"
               v-if="showSortOption"
          >
            <button v-for="(item, i) in sortOptions"
                    style="color: #2E3842; border-color: #E6EBEF;"
                    class="w-full h-9 px-2 text-left text-sm font-medium"
                    :key="i"
                    :class="[{'border-t': i > 0}]"
                    @click="onClickSortButton(item.value)">
              {{item.title}}
            </button>
          </div>
        </div>
      </div>
      <div class="min-w-0 flex-1 relative"
           :class="{
             'mt-6': chuckedMeetingNoteList && chuckedMeetingNoteList.length > 0
           }"
      >
        <perfect-scrollbar v-if="chuckedMeetingNoteList && chuckedMeetingNoteList.length > 0"
                           class="absolute inset-0"
        >
          <div class="space-y-10 pb-10">
            <RoomMeetingNoteList v-for="meetingNoteList in chuckedMeetingNoteList"
                                 :meetingNoteList="meetingNoteList"
                                 :key="meetingNoteList[0]?.meetingNoteId"
                                 @click="onClickMeetingNote"
            />
          </div>
        </perfect-scrollbar>
        <div class="h-full flex flex-col justify-center items-center gap-y-10"
             v-if="meetingNoteList.length < 1">
          <div style="width: 300px; height: 300px;">
            <GrmIcon icon="meeting-note-init" size="full"/>
          </div>
          <p style="color: #4A5A69;" class="text-center text-lg leading-7" v-html="$t('extendMenu.meetingNote.ai.description')" />
          <button style="height: 52px;"
                  class="btn pr-4 rounded-lg flex items-center gap-x-1 text-white font-bold"
                  @click="onStartNewMeetingNote"
          >
            <div style="width: 50px; height: 50px;"
                 class="flex justify-center items-center pointer-events-none"
            >
              <img width="28" height="28" :src="require('@/assets/images/meeting-note/add_white.svg')" />
            </div>
            {{$t('extendMenu.meetingNote.newMeetingNote')}}
          </button>
        </div>
      </div>
    </div>

    <transition name="fade">
      <RoomMeetingNoteDetail v-if="isOpenNoteDetail"
          class="slide-right"
          :meetingNote="selectedNote"
          @goBack="onClickGoBack"
          @close="onClose"
      />
    </transition>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import GrmIcon from "@/components/Common/icon/GrmIcon.vue";
import RoomMeetingNoteList from "@/components/Room/MeetingNote/List.vue";
import RoomMeetingNoteDetail from "@/components/Room/MeetingNote/Detail/NoteDetail.vue";

export default {
  name: 'RoomMeetingNoteModal',
  components: {
    RoomMeetingNoteList,
    GrmIcon,
    RoomMeetingNoteDetail
  },
  data(){
    return {
      doNotShowAgain: false,
      isOpenNoteDetail: false,
      selectedNote: null,
      selectedSortOption: "desc",
      showSortOption: false,
    }
  },
  computed: {
    ...mapGetters('meetingNote', [
      'meetingNoteList',
      'ongoingMeetingNote',
      'isOngoingMeetingNote',
    ]),
    sortOptions() {
      return [
        { value: 'desc', title: this.$t('extendMenu.meetingNote.sort.dateDesc') },
        { value: 'asc', title: this.$t('extendMenu.meetingNote.sort.dateAsc') }
      ]
    },
    isSameDay(){
      return (a, b) => {
        const date1 = new Date(a.startDate)
        const date2 = new Date(b.startDate)
        if(date1.getFullYear() === date2.getFullYear()
            && date1.getMonth() === date2.getMonth()
            && date1.getDate() === date2.getDate()){
          return true;
        }else{
          return false
        }
      }
    },
    ongoingMeetingNoteList(){
      if(!this.meetingNoteList || this.meetingNoteList.length < 1) return;

      return this.meetingNoteList.filter(p => {
        return !p.endDate;
      })
    },
    chuckedMeetingNoteList(){
      if(!this.sortedMeetingNoteList || this.sortedMeetingNoteList.length < 1) return;

      let lastNote = [this.sortedMeetingNoteList[0]];
      const arr = [lastNote]
      for(let i = 1; i < this.sortedMeetingNoteList.length; i++) {
        if(this.isSameDay(lastNote[0], this.sortedMeetingNoteList[i])){
          lastNote.push(this.sortedMeetingNoteList[i])
        }else{
          lastNote = [this.sortedMeetingNoteList[i]];
          arr.push(lastNote);
        }
      }
      
      return arr
    },
    sortedMeetingNoteList(){
      if(!this.meetingNoteList || this.meetingNoteList.length < 1) return;
      let sorted = this.meetingNoteList.filter(p => {
        // 정책 추가
        return !!p.endDate;
      }).sort((a, b) => {
        if(a.startDate < b.startDate)
          return -1;
      })
      
      if(this.selectedSortOption === 'desc'){
        return sorted.reverse()
      }else{
        return sorted
      }
    },
    selectedSortOptionTitle(){
      return this.sortOptions.find(opt=>opt.value === this.selectedSortOption)?.title;
    },
  },
  methods: {
    ...mapActions('meetingNote', [
      'startMeetingNote',
      'stopMeetingNote',
      'initMeetingNote',
    ]),
    onClose() {
      this.$modal('RoomMeetingNoteModal')
    },
    onStartNewMeetingNote() {
      if(this.isOngoingMeetingNote){
        return this.$alert({
          text: this.$t('extendMenu.meetingNote.done'),
          onlyOk: true,
        })
      }
      if(this.doNotShowAgain){
        this.startNewMeetingNote();
      }else{
        this.$alert({
          title: this.$t('extendMenu.meetingNote.startRecording'),
          text: this.$t('extendMenu.meetingNote.startRecordingText'),
          type: 'checkbox',
          typeDescription: this.$t('extendMenu.meetingNote.hidePopup'),
          ok: (check) => {
            this.doNotShowAgain = check;
            this.startNewMeetingNote({title:''});
          },
        })
      }
    },
    startNewMeetingNote(){
      if(this.isOngoingMeetingNote){
        this.stopMeetingNote(this.ongoingMeetingNote)
      }else{
        this.startMeetingNote()
      }
    },
    onClickSortButton(value){
      this.showSortOption = false
      this.selectedSortOption = value
    },
    onClickMeetingNote(meetingNote){
      this.isOpenNoteDetail = !this.isOpenNoteDetail
      if(!meetingNote.stt)
        this.initMeetingNote(meetingNote)
      this.selectedNote = meetingNote
    },
    onClickGoBack(){
      this.isOpenNoteDetail = !this.isOpenNoteDetail
      this.selectedNote = null;
    }
  }
}
</script>

<style lang="scss" scoped>
.room-meeting-note-modal-component {
  width: 480px;
  background-color: #fff;
  position: absolute;
  top: 50px;
  bottom: 0;
  left: 60px;
  z-index: 1;
}

.btn {
  background-color: #6597F8;

  &:hover {
    background-color: #2D58ED;
  }
}
</style>